import { useState } from "react";
// React Query
import { useQuery } from "@tanstack/react-query";

// MUI
import { Box, Dialog, DialogContent, DialogTitle, Divider, Icon, IconButton, Skeleton, Stack, Typography } from "@mui/material";

// API
import { getVehicleGPSDetails } from "@DataPages/api/inventory/queries";

// Helpers
import { v4 as uuidv4 } from "uuid";

// Components
import DialogNavigation from "@DataPages/components/Inventory/Layout/DialogNavigation";
import vehicleColumnDetail from "@DataPages/components/Inventory/Widgets/VehicleColumnDetail";
import SimpleIconButton from "@Layouts/components/Buttons/SimpleIconButton.tsx";
import TransferGPSDeviceSection from "@DataPages/components/Inventory/Widgets/TransferGPSDeviceSection.tsx";

// External Components
import Masonry from "react-masonry-css";
// Icons
import { ReactComponent as CloseIcon } from "@/assets/icons/dataPages/inventory/close-icon.svg";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import BlockIcon from "@/assets/icons/svg/blockIcon.svg";

// Types
import { Inventory } from "@DataPages/types/inventory/models/inventory.ts";

type AssetLocationDialogProps = {
	rows: Inventory[];
	isOpen: boolean;
	onClose: () => void;
	assetLocation: string;
	selectedVehicle: Inventory;
	locationId: string;
	accountId: string;
	onNavigate: (direction: "prev" | "next") => void;
	disabledButtons: { prev: boolean; next: boolean };
};

const randomHeightArray = [
	{
		_id: 1,
		height: 240,
	},
	{
		_id: 2,
		height: 240,
	},
	{
		_id: 3,
		height: 240,
	},
	{
		_id: 4,
		height: 240,
	},
];

const getGpsUrlComponent = (sourceUrl: string) => {
	if (sourceUrl.toUpperCase() === "ACCESS DENIED") {
		return (
			<Box
				style={{
					width: "100%",
					minHeight: "328px",
					paddingBottom: "50px",
					backgroundColor: "#CCCCCC",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Typography className={"!font-montserrat !text-[18px] !text-[#666666] !mb-[20px] !font-medium"}>Access Denied</Typography>
				<Icon>
					<img src={BlockIcon} className={"!text-[32px] !text-[#666666] !mb-[-32px]"} alt="month-pace-pvr" />
				</Icon>
			</Box>
		);
	} else if (sourceUrl?.startsWith("http")) {
		return (
			<iframe
				src={sourceUrl}
				title="Embedded Asset Location Management"
				style={{ width: "100%", minHeight: "780px", paddingBottom: "50px" }}
			/>
		);
	}
};

export default function GPSInformationDialog(props: Readonly<AssetLocationDialogProps>) {
	const [expanded, setExpanded] = useState(true);

	/******************************************** QUERIES ********************************************/
	const { data, isLoading } = useQuery({
		queryKey: ["gpsInformationDetail", props.selectedVehicle?.vin],
		queryFn: () => getVehicleGPSDetails(props.locationId, props.selectedVehicle?._id, props.accountId) as Promise<Inventory>,
		enabled: Boolean(props.locationId) && Boolean(props.isOpen) && Boolean(props.selectedVehicle?._id),
	});

	/******************************************* HANDLERS *******************************************/
	const handleExpandToggle = () => {
		setExpanded((prev) => !prev);
	};

	return (
		<Dialog
			open={props.isOpen}
			onClose={props.onClose}
			maxWidth={"xl"}
			fullWidth
			scroll="paper"
			aria-labelledby="masonry-dialog-title"
			aria-describedby="masonry-dialog-description"
			PaperProps={{
				style: { width: "1405px", maxHeight: "870px" },
			}}
			classes={{
				paper: "!rounded-[10px]",
			}}
		>
			<DialogTitle
				classes={{
					root: "!ml-2 !pb-4",
				}}
			>
				<Stack direction={"row"} justifyContent={"space-between"} mt={"-3px"}>
					<Stack direction={"row"} alignItems={"center"}>
						<Typography className={"!font-montserrat !text-[18px] !text-[#666666]"}>
							{`${props.selectedVehicle?.year} ${props.selectedVehicle?.make} ${props.selectedVehicle?.model} \
						${(props.selectedVehicle?.trim as string) ?? ""} - Stock# ${props.selectedVehicle?.stockId}`}
						</Typography>
						<IconButton size="small" aria-label="expand" onClick={handleExpandToggle} className={"!ml-[4px]"}>
							<ArrowForwardIosRoundedIcon className={expanded ? "arrow-icon-point-up" : "arrow-icon-point-down"} />
						</IconButton>
					</Stack>
					<SimpleIconButton
						className={"!absolute top-4 right-4"}
						aria-label="close"
						onClick={props.onClose}
						icon={<CloseIcon className=" !text-[#B3B3B3]" />}
					/>
				</Stack>
			</DialogTitle>
			<DialogContent classes={{ root: "flex flex-col overflow-y-auto flex-grow max-h-[800px] !ml-2 mr-2" }}>
				{expanded && (
					<Masonry breakpointCols={4} className="my-masonry-grid" columnClassName="my-masonry-grid_column">
						{isLoading
							? randomHeightArray.map((item, idx) => (
									<Box key={item._id} width={250}>
										<Skeleton variant={"text"} width={120} className={"rounded-md !mb-3"} />
										<Skeleton
											variant={"rectangular"}
											width={250}
											height={randomHeightArray[idx].height}
											className={"rounded-sm"}
										/>
									</Box>
							  ))
							: vehicleColumnDetail({
									data,
									inventoryId: props.selectedVehicle?._id,
									gpsInconsistency: Boolean(props.selectedVehicle?.gpsInconsistency),
									expandedRows: true,
									customGPSDeviceSection: (
										<TransferGPSDeviceSection
											inventoryId={props.selectedVehicle?._id}
											gpsInconsistency={Boolean(props.selectedVehicle?.gpsInconsistency)}
										/>
									),
							  })?.map((item) => {
									return <Box key={uuidv4()}>{item}</Box>;
							  })}
					</Masonry>
				)}
				<Divider variant={"fullWidth"} className={"!mt-[-15px] !mb-[10px]"} />
				{/*TODO: Change the src for the real one*/}
				{isLoading ? <Skeleton variant="rectangular" width="100%" height="500px" /> : getGpsUrlComponent(data?.sourceUrl ?? "")}
				<DialogNavigation
					className="!absolute bottom-0 left-0 right-0 bg-white py-4"
					onPrev={() => props.onNavigate("prev")}
					onNext={() => props.onNavigate("next")}
					disabledButtons={props.disabledButtons}
				/>
			</DialogContent>
		</Dialog>
	);
}
